import { PARTY_SIZE } from "../party/constants.ts";
import type { ColourRoleName } from "../ui/tokens.ts";
import { VISION_CAP_TILES, type Size } from "./vision.ts";

/**
 * The minimap's geometry and what it may show (P1.9-T52, R-0055; ADR-061 decisions 1 and 6; ADR-054): pure data, so
 * the page and its tests read one rule. The field draws the map's ground across the whole field (P1.9-T178), but shows
 * marks only for people within the player's vision, the cap around their character, whatever the field's size or zoom, and the map server's margin
 * beyond the cap is never a reason to draw a mark (the same reason a nameplate hides there). Zones are never sent
 * (ADR-054), so the field has no zone overlay.
 */

/**
 * The minimap field in the HUD, in logical pixels: the slot's 76 by 76 less the frame's 3 px inset each side.
 */
export const MINIMAP_FIELD: Size = Object.freeze({ width: 70, height: 70 });

/**
 * The field of the expanded Nearby window, in logical pixels.
 */
export const NEARBY_FIELD: Size = Object.freeze({ width: 288, height: 164 });

/**
 * The field's scale in logical pixels a tile, by zoom step: zoom out is the first, zoom in the last.
 */
export const MINIMAP_SCALES: readonly number[] = Object.freeze([1, 2, 3]);

/**
 * The expanded field's scale in logical pixels a tile, by the same steps.
 */
export const MINIMAP_BIG_SCALES: readonly number[] = Object.freeze([6, 8, 10]);

/**
 * The zoom step a viewer has before choosing: the second, 2 px a tile.
 */
export const DEFAULT_MINIMAP_STEP = 1;

/**
 * What a mark stands for: the player, a party member, a known place or the player's pin.
 */
export type MinimapMarkKind = "self" | "party" | "place" | "pin";

/**
 * How near a known place may be to the pin, in tiles, before the pin stands for it and the place has neither a mark nor
 * a compass pip.
 */
export const MINIMAP_PIN_STANDS_FOR_TILES = 2;

/**
 * A place the character knows, as the minimap holds it: the handle and name the Map lists it by, and its position in
 * tiles. Only a discovered place is ever given.
 */
export interface MinimapPlaceSpot {
  readonly handle: number;
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/**
 * How many places a party roster has: its six seats (`PARTY_SIZE`, ADR-101 decision 1), numbered 0 to 5. ADR-100's "up to five members" counts the other members a player sees, one fewer.
 */
export const PARTY_PLACES = PARTY_SIZE;

/**
 * The colour of each roster place (ADR-061's note of 2026-10-14, P1.9-T180): six design tokens no other mark uses, so a
 * member keeps their colour for as long as they are in the party and is never read as the player or as another kind.
 */
export const PARTY_COLOURS: readonly ColourRoleName[] = Object.freeze(["party-1", "party-2", "party-3", "party-4", "party-5", "party-6"] as const);

/**
 * The colour token of a party member's roster place.
 *
 * @param place The member's roster place, 0 to 5.
 * @returns The colour token.
 * @throws RangeError when the place is not a whole number from 0 to 5.
 */
export function partyColourOf(place: number): ColourRoleName {
  if (!Number.isInteger(place) || place < 0 || place >= PARTY_PLACES) {
    throw new RangeError(`party place ${place} is outside the roster of ${PARTY_PLACES}`);
  }
  return PARTY_COLOURS[place] as ColourRoleName;
}

/**
 * Someone the field may draw besides the player: a handle, a position in tiles, the name the Nearby window shows
 * beside the mark and the member's roster place, 0 to 5, which gives their colour (`partyColourOf`).
 */
export interface MinimapSpot {
  readonly handle: number;
  readonly x: number;
  readonly y: number;
  readonly name: string;
  readonly place: number;
}

/**
 * One mark in a field: its kind, the handle and name it stands for (0 and empty for the player), the roster place that
 * gives a party member's colour (0 for the player), the top left of its box in the field's logical pixels and the box's
 * size. A party member is the box filled; the player is a disc drawn about the same box's centre; the pin is a teardrop
 * in a box 5 wide whose bottom row is the pin's own pixel.
 */
export interface MinimapMark {
  readonly kind: MinimapMarkKind;
  readonly handle: number;
  readonly name: string;
  readonly place: number;
  readonly left: number;
  readonly top: number;
  readonly size: number;
}

/**
 * The scale of a field at a zoom step.
 *
 * @param step The step, 0 to 2.
 * @param expanded Whether it is the Nearby window's field.
 * @returns Logical pixels a tile.
 */
export function minimapScale(step: number, expanded: boolean): number {
  return (expanded ? MINIMAP_BIG_SCALES : MINIMAP_SCALES)[step] ?? (expanded ? MINIMAP_BIG_SCALES : MINIMAP_SCALES)[DEFAULT_MINIMAP_STEP] ?? 1;
}

/**
 * One step of zoom, stopping at the ends.
 *
 * @param step The current step, 0 to 2.
 * @param direction "in" for the larger scale, "out" for the smaller.
 * @returns The new step.
 */
export function stepMinimapZoom(step: number, direction: "in" | "out"): number {
  return Math.max(0, Math.min(MINIMAP_SCALES.length - 1, step + (direction === "in" ? 1 : -1)));
}

/**
 * Reads a stored zoom step.
 *
 * @param raw The stored text, or null for none.
 * @returns 0, 1 or 2 as stored, and the default step for anything else.
 */
export function parseMinimapStep(raw: string | null): number {
  return raw === "0" || raw === "1" || raw === "2" ? Number(raw) : DEFAULT_MINIMAP_STEP;
}

/**
 * Whether an offset from the player lies within the player's vision, `VISION_CAP_TILES` around them, the edge
 * included.
 *
 * @param dx Tiles across from the player.
 * @param dy Tiles down from the player.
 * @returns True within the cap.
 */
export function inMinimapVision(dx: number, dy: number): boolean {
  return Math.abs(dx) <= VISION_CAP_TILES.width / 2 && Math.abs(dy) <= VISION_CAP_TILES.height / 2;
}

/**
 * Whether a target at an offset from the player has its centre inside a field at a scale, by the rounding the marks
 * use: the centre pixel is half the field, rounded, and the field holds the pixels from 0 up to its size.
 *
 * @param dx Tiles across from the player.
 * @param dy Tiles down from the player.
 * @param field The field's size in logical pixels.
 * @param scale Logical pixels a tile.
 * @returns True when the rounded pixel lies inside the field.
 */
export function inMinimapField(dx: number, dy: number, field: Size, scale: number): boolean {
  const x = Math.round(field.width / 2 + dx * scale);
  const y = Math.round(field.height / 2 + dy * scale);
  return x >= 0 && y >= 0 && x < field.width && y < field.height;
}

/**
 * The marks of a field: the player in the middle, then each of `others` that lies within vision and whose centre
 * falls inside the field at this scale. Nobody outside vision is drawn, and nothing is drawn for anyone who is not
 * given (the page gives party members only, as the row that makes them arrives). The pin, when given, is the last mark
 * and is not bound by vision, as the player knows where their own pin is: a teardrop 5 wide and 5 tall, tip on the pin's
 * pixel, wherever its centre falls inside the field (`hasMark`).
 *
 * @param self The player's position in tiles.
 * @param others Who else may be drawn, positions in tiles.
 * @param field The field's size in logical pixels.
 * @param scale Logical pixels a tile.
 * @param expanded Whether the field is the Nearby window's, whose party marks are larger.
 * @param pin The player's pin in tiles, or null for none.
 * @param places The known places to draw, a diamond 5 wide centred on each place's pixel wherever its centre falls
 *   inside the field (vision does not apply), none within `MINIMAP_PIN_STANDS_FOR_TILES` of the pin; ignored in the
 *   Nearby field. They follow the player's mark, so the party and the pin are drawn over them.
 * @returns The player's mark first, then the places', the others' in the order given, then the pin's.
 * @throws RangeError when a member's place is not a roster place.
 */
export function minimapMarks(self: { readonly x: number; readonly y: number }, others: readonly MinimapSpot[], field: Size, scale: number, expanded: boolean, pin: { readonly x: number; readonly y: number } | null = null, places: readonly MinimapPlaceSpot[] = []): MinimapMark[] {
  const centreX = field.width / 2;
  const centreY = field.height / 2;
  const selfSize = 3;
  const partySize = expanded ? 5 : 3;
  const marks: MinimapMark[] = [{ kind: "self", handle: 0, name: "", place: 0, left: Math.round(centreX) - Math.floor(selfSize / 2), top: Math.round(centreY) - Math.floor(selfSize / 2), size: selfSize }];
  if (!expanded) {
    for (const place of places) {
      const dx = place.x - self.x;
      const dy = place.y - self.y;
      if (!inMinimapField(dx, dy, field, scale) || (pin !== null && (place.x - pin.x) ** 2 + (place.y - pin.y) ** 2 <= MINIMAP_PIN_STANDS_FOR_TILES ** 2)) {
        continue;
      }
      const x = Math.round(centreX + dx * scale);
      const y = Math.round(centreY + dy * scale);
      marks.push({ kind: "place", handle: place.handle, name: place.name, place: 0, left: x - 2, top: y - 2, size: 5 });
    }
  }
  for (const other of others) {
    partyColourOf(other.place);
    const dx = other.x - self.x;
    const dy = other.y - self.y;
    if (!inMinimapVision(dx, dy) || !inMinimapField(dx, dy, field, scale)) {
      continue;
    }
    const x = Math.round(centreX + dx * scale);
    const y = Math.round(centreY + dy * scale);
    marks.push({ kind: "party", handle: other.handle, name: other.name, place: other.place, left: x - Math.floor(partySize / 2), top: y - Math.floor(partySize / 2), size: partySize });
  }
  if (pin !== null) {
    const dx = pin.x - self.x;
    const dy = pin.y - self.y;
    if (inMinimapField(dx, dy, field, scale)) {
      const x = Math.round(centreX + dx * scale);
      const y = Math.round(centreY + dy * scale);
      marks.push({ kind: "pin", handle: 0, name: "", place: 0, left: x - 2, top: y - 4, size: 5 });
    }
  }
  return marks;
}
